import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';

export function ShaderAnimation() {
    const containerRef = useRef(null);
    const sceneRef = useRef(null);
    const [isVisible, setIsVisible] = useState(true);

    useEffect(() => {
        // Fade out after 4 seconds to show the effect only once per load/refresh
        const timer = setTimeout(() => {
            setIsVisible(false);
        }, 4000);

        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (!containerRef.current) return;

        const container = containerRef.current;

        // Vertex shader
        const vertexShader = `
      void main() {
        gl_Position = vec4( position, 1.0 );
      }
    `;

        // Fragment shader
        const fragmentShader = `
      #define TWO_PI 6.2831853072
      #define PI 3.14159265359

      precision highp float;
      uniform vec2 resolution;
      uniform float time;

      void main(void) {
        vec2 uv = (gl_FragCoord.xy * 2.0 - resolution.xy) / min(resolution.x, resolution.y);
        float t = time*0.05;
        float lineWidth = 0.002;

        vec3 color = vec3(0.0);
        for(int j = 0; j < 3; j++){
          for(int i=0; i < 5; i++){
            color[j] += lineWidth*float(i*i) / abs(fract(t - 0.01*float(j)+float(i)*0.01)*5.0 - length(uv) + mod(uv.x+uv.y, 0.2));
          }
        }
        
        gl_FragColor = vec4(color[0],color[1],color[2],1.0);
      }
    `;

        // Initialize Three.js scene
        const camera = new THREE.Camera();
        camera.position.z = 1;

        const scene = new THREE.Scene();
        const geometry = new THREE.PlaneGeometry(2, 2);

        const uniforms = {
            time: { type: 'f', value: 1.0 },
            resolution: { type: 'v2', value: new THREE.Vector2() },
        };

        const material = new THREE.ShaderMaterial({
            uniforms: uniforms,
            vertexShader: vertexShader,
            fragmentShader: fragmentShader,
        });

        const mesh = new THREE.Mesh(geometry, material);
        scene.add(mesh);

        const renderer = new THREE.WebGLRenderer({ antialias: true });
        renderer.setPixelRatio(window.devicePixelRatio);

        container.appendChild(renderer.domElement);

        // Handle window resize
        const onWindowResize = () => {
            const width = window.innerWidth;
            const height = window.innerHeight;
            renderer.setSize(width, height);
            // Use renderer.domElement dimensions for uniform resolution (physical pixels)
            uniforms.resolution.value.x = renderer.domElement.width;
            uniforms.resolution.value.y = renderer.domElement.height;
        };

        // Initial resize
        onWindowResize();
        window.addEventListener('resize', onWindowResize, false);

        // Animation loop
        const animate = () => {
            // Stop animation loop if component is unmounting
            if (!sceneRef.current) return;

            const animationId = requestAnimationFrame(animate);
            uniforms.time.value += 0.05;
            renderer.render(scene, camera);

            sceneRef.current.animationId = animationId;
        };

        // Store scene references for cleanup
        sceneRef.current = {
            camera,
            scene,
            renderer,
            uniforms,
            animationId: 0,
        };

        // Start animation
        animate();

        // Cleanup function
        return () => {
            window.removeEventListener('resize', onWindowResize);

            if (sceneRef.current) {
                cancelAnimationFrame(sceneRef.current.animationId);

                if (container && sceneRef.current.renderer.domElement) {
                    container.removeChild(sceneRef.current.renderer.domElement);
                }

                sceneRef.current.renderer.dispose();
                geometry.dispose();
                material.dispose();
                sceneRef.current = null;
            }
        };
    }, []);

    return (
        <div
            ref={containerRef}
            className={`shader-animation-container ${!isVisible ? 'fade-out' : ''}`}
        />
    );
}

export default ShaderAnimation;
